import React, { Suspense } from 'react'
import { useLingui } from '@lingui/react/macro'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams, useSearchParams } from 'react-router-dom'

import { YStack } from 'tamagui'

import { getSlugFromPrettyUrl } from '@data/util/urlHelpers'

import { EventMetadata } from '@newComponents/Event/EventMetadata'
import { Card } from '@newComponents/Card'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Spinner } from '@newComponents/Spinner'

import { EventItem } from '@newComponents/Event/EventItem'
import { lazy as loadable } from '@loadable/component'

import { EventAttendContent } from './EventAttendContent'

import { EventAttendScreenQuery } from './__generated__/EventAttendScreenQuery.graphql'

const EventAttendFormDialog = loadable(
  () => import('../../modals/EventAttendForm/EventAttendFormDialog')
)

export const EventAttendScreen = () => {
  const { t } = useLingui()
  const params = useParams<'eventId'>()
  const [searchParams] = useSearchParams()

  const data = useLazyLoadQuery<EventAttendScreenQuery>(
    graphql`
      query EventAttendScreenQuery($slug: String!) {
        event: eventBySlug(slug: $slug) {
          name

          # Screen-level fields for page title and conditional rendering
          eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
            totalCount
          }
          myAttendance {
            visibleStatus
            paymentStatus
          }
          attendeeForm {
            rowId
          }

          ...EventItemNew_event
          ...EventAttendContent_event
          ...EventMetadata_event
        }

        ...EventItemNew_query
        ...EventAttendContent_query
      }
    `,
    { slug: getSlugFromPrettyUrl(params.eventId) }
  )

  const statusParam = searchParams.get('status')

  if (!data.event) {
    return null
  }

  const pageTitle =
    data.event.eventTicketingTiers.totalCount > 0 &&
    data.event.myAttendance?.paymentStatus !== 'SUCCEEDED'
      ? t`Confirm Attendance and Select Tickets for ${data.event.name}`
      : t`Confirm Attendance for ${data.event.name}`

  return (
    <>
      <EventMetadata title={pageTitle} event={data.event} />

      <GradientBackgroundScreen alignItems='center' gap='$8'>
        <EventItem
          event={data.event}
          query={data}
          maxWidth={600}
          width='100%'
          showAttendButton={false}
          displayFullDate
          displayVisibility={false}
        />

        {data.event.attendeeForm &&
        statusParam !== 'NOT_ATTENDING' &&
        data.event.myAttendance?.visibleStatus !== 'PENDING_APPROVAL' ? (
          <Card gap='$6' maxWidth={600} width='100%'>
            <Suspense
              fallback={
                <YStack flex={1} alignItems='center' justifyContent='center'>
                  <Spinner size='$9' color='$primaryColor' />
                </YStack>
              }
            >
              <EventAttendFormDialog />
            </Suspense>
          </Card>
        ) : (
          <EventAttendContent
            event={data.event}
            query={data}
            maxWidth={600}
            width='100%'
          />
        )}
      </GradientBackgroundScreen>
    </>
  )
}

export default EventAttendScreen
